import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {
  Text,
  Appbar,
  Button,
  Divider,
  Snackbar,
  RadioButton,
  ActivityIndicator,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute, CommonActions} from '@react-navigation/native';
import WebView from 'react-native-webview';
import {useSubscriptionStore} from '../../store/useSubscriptionStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const PaymentGatewayScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {
    orgUnitId,
    orgUnitName,
    subscriptionId,
    planFamily,
    planVariant,
    invoiceAmount,
    netPrice,
    taxAmount,
    discountAmount,
  } = route.params || {};

  const {initiatePayment, verifyPayment} = useSubscriptionStore();
  const {fetchOrgUnits} = useOrgUnitStore();

  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [txnId, setTxnId] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handlePayNow = useCallback(async () => {
    setProcessing(true);
    try {
      const result = await initiatePayment(orgUnitId, planVariant.id, subscriptionId);
      setTxnId(result.txnId);
      setPaymentUrl(result.paymentUrl);
    } catch (e) {
      setSnackbar(
        e.response?.data?.message || e.response?.data?.error?.message || t('common.error'),
      );
      setProcessing(false);
    }
  }, [orgUnitId, planVariant, subscriptionId, initiatePayment, t]);

  const handlePaymentComplete = useCallback(async (success) => {
    setPaymentUrl(null);

    if (!success) {
      setProcessing(false);
      setSnackbar(t('subscription.paymentFailed'));
      return;
    }

    // Verify payment with backend
    setVerifying(true);
    try {
      await verifyPayment(txnId);
      await fetchOrgUnits();

      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{name: 'StoreList'}],
        }),
      );
    } catch (e) {
      setSnackbar(
        e.response?.data?.message || e.response?.data?.error?.message || t('common.error'),
      );
    } finally {
      setProcessing(false);
      setVerifying(false);
    }
  }, [txnId, verifyPayment, fetchOrgUnits, navigation, t]);

  const handleWebViewNavigationChange = useCallback((navState) => {
    const {url} = navState;
    if (!url) return;

    // Detect callback URL — Easebuzz redirects to surl/furl after payment
    if (url.includes('tailorapp.com/payment/callback')) {
      // Payment completed — determine success/failure from the page content
      // The WebView will POST to our callback URL; we intercept and verify via API
      handlePaymentComplete(true);
    }
  }, [handlePaymentComplete]);

  // If WebView is showing, render it full-screen
  if (paymentUrl) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction
            onPress={() => {
              setPaymentUrl(null);
              setProcessing(false);
            }}
            color={colors.white}
          />
          <Appbar.Content
            title={t('subscription.payment')}
            titleStyle={styles.appbarTitle}
          />
        </Appbar.Header>
        <WebView
          source={{uri: paymentUrl}}
          style={styles.webview}
          onNavigationStateChange={handleWebViewNavigationChange}
          onShouldStartLoadWithRequest={(request) => {
            if (request.url.includes('tailorapp.com/payment/callback')) {
              handlePaymentComplete(true);
              return false;
            }
            return true;
          }}
          javaScriptEnabled
          domStorageEnabled
          startInLoadingState
          renderLoading={() => (
            <View style={styles.webviewLoading}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.loadingText}>{t('subscription.loadingPayment')}</Text>
            </View>
          )}
          onError={() => {
            handlePaymentComplete(false);
          }}
          onHttpError={(syntheticEvent) => {
            const {nativeEvent} = syntheticEvent;
            if (nativeEvent.statusCode >= 400) {
              handlePaymentComplete(false);
            }
          }}
        />
      </View>
    );
  }

  // Verifying state
  if (verifying) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.verifyingText}>{t('subscription.verifyingPayment')}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
          disabled={processing}
        />
        <Appbar.Content
          title={t('subscription.payment')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Order summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.sectionTitle}>{t('subscription.orderSummary')}</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{planFamily?.name} Plan</Text>
            <Text style={styles.summaryValue}>
              {'\u20B9'}{netPrice?.toFixed(2)}
            </Text>
          </View>
          {discountAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text style={[styles.summaryLabel, styles.discountText]}>
                {t('subscription.discount')}
              </Text>
              <Text style={[styles.summaryValue, styles.discountText]}>
                -{'\u20B9'}{discountAmount?.toFixed(2)}
              </Text>
            </View>
          )}
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('subscription.gst')} (18%)</Text>
            <Text style={styles.summaryValue}>
              {'\u20B9'}{taxAmount?.toFixed(2)}
            </Text>
          </View>
          <Divider style={{marginVertical: spacing.sm}} />
          <View style={styles.summaryRow}>
            <Text style={styles.totalLabel}>{t('subscription.total')}</Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{invoiceAmount?.toFixed(2)}
            </Text>
          </View>
        </View>

        {/* Easebuzz handles payment method selection */}
        <View style={styles.gatewayInfo}>
          <MaterialCommunityIcons
            name="credit-card-check-outline"
            size={28}
            color={colors.primary}
          />
          <View style={styles.gatewayInfoText}>
            <Text style={styles.gatewayTitle}>{t('subscription.secureGateway')}</Text>
            <Text style={styles.gatewayDesc}>{t('subscription.gatewayDesc')}</Text>
          </View>
        </View>

        {/* Security note */}
        <View style={styles.securityNote}>
          <MaterialCommunityIcons
            name="shield-check"
            size={18}
            color={colors.success}
          />
          <Text style={styles.securityText}>
            {t('subscription.securePayment')}
          </Text>
        </View>
      </ScrollView>

      {/* Pay button */}
      <View style={styles.bottomBar}>
        <Button
          mode="contained"
          onPress={handlePayNow}
          loading={processing}
          disabled={processing}
          buttonColor={colors.primary}
          contentStyle={styles.payButtonContent}
          style={styles.payButton}
          icon="lock">
          {processing
            ? t('subscription.processing')
            : `${t('subscription.payNow')} \u20B9${invoiceAmount?.toFixed(2)}`}
        </Button>
      </View>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {flex: 1, padding: spacing.md},
  centerContent: {justifyContent: 'center', alignItems: 'center'},

  // WebView
  webview: {flex: 1},
  webviewLoading: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: 14,
    color: colors.textSecondary,
  },
  verifyingText: {
    marginTop: spacing.md,
    fontSize: 16,
    color: colors.text,
    fontWeight: '600',
  },

  // Summary card
  summaryCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  summaryLabel: {fontSize: 14, color: colors.textSecondary},
  summaryValue: {fontSize: 14, color: colors.text},
  discountText: {color: colors.success, fontWeight: '500'},
  totalLabel: {fontSize: 16, fontWeight: '700', color: colors.text},
  totalValue: {fontSize: 18, fontWeight: '700', color: colors.primary},

  // Gateway info
  gatewayInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.md,
    gap: spacing.md,
  },
  gatewayInfoText: {flex: 1},
  gatewayTitle: {fontSize: 15, fontWeight: '600', color: colors.text},
  gatewayDesc: {fontSize: 13, color: colors.textSecondary, marginTop: 2},

  // Security
  securityNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    marginBottom: spacing.xxl,
  },
  securityText: {
    fontSize: 13,
    color: colors.success,
  },

  // Bottom
  bottomBar: {
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  payButton: {borderRadius: 8},
  payButtonContent: {paddingVertical: spacing.xs},
});

export default PaymentGatewayScreen;
